import { useState, useCallback, useMemo } from 'react';
import { t } from '@lingui/core/macro';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import type { Folder } from '../../types/api';
import { useReorderFolders } from '../../services/folders';
import { useCurrentUser } from '../../services/userHooks';
import { useOptionalBookmarkDnd } from '../../contexts/BookmarkDndContext';
import SortableFolderItem from './SortableFolderItem';
import FolderItem from './FolderItem';
import FolderDragOverlay from './FolderDragOverlay';
import type { DropIndicatorPosition } from './SortableFolderItem';

export interface SortableFolderListProps {
  folders: Folder[];
  currentFolderId?: string;
  sortingEnabled?: boolean;
}

interface FolderDragData {
  type: 'folder-sort';
  folder: Folder;
}

/** Nested DndContext for folder sorting, independent from bookmark drag & drop */
function SortableFolderList({
  folders,
  currentFolderId,
  sortingEnabled = true,
}: SortableFolderListProps) {
  const { currentUser } = useCurrentUser();
  const reorderFolders = useReorderFolders();
  const bookmarkDnd = useOptionalBookmarkDnd();
  const isBookmarkBeingDragged = bookmarkDnd?.isDragging ?? false;

  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeFolder, setActiveFolder] = useState<Folder | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const [justMovedId, setJustMovedId] = useState<string | null>(null);

  const resetDragState = useCallback(() => {
    setActiveId(null);
    setActiveFolder(null);
    setOverId(null);
  }, []);

  const isSortingEnabled = sortingEnabled && folders.length > 1;
  const folderIds = useMemo(() => folders.map((f) => f.id), [folders]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const id = event.active.id as string;
      setActiveId(id);
      setActiveFolder(folders.find((f) => f.id === id) ?? null);
    },
    [folders],
  );

  const handleDragOver = useCallback((event: DragOverEvent) => {
    setOverId((event.over?.id as string) ?? null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      resetDragState();

      if (!over || active.id === over.id) return;

      const overData = over.data.current as FolderDragData | undefined;
      if (overData?.type !== 'folder-sort') return;

      const oldIndex = folders.findIndex((f) => f.id === active.id);
      const newIndex = folders.findIndex((f) => f.id === over.id);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

      const newFolderIds = arrayMove(folders, oldIndex, newIndex).map((f) => f.id);

      setJustMovedId(active.id as string);
      setTimeout(() => setJustMovedId(null), 200);

      reorderFolders.mutate({
        folderIds: newFolderIds,
        formKey: currentUser?.formKey ?? '',
      });
    },
    [folders, reorderFolders, currentUser?.formKey, resetDragState],
  );

  const handleDragCancel = resetDragState;

  const getDropIndicatorPosition = useCallback(
    (folderId: string): DropIndicatorPosition => {
      if (!activeId || !overId || activeId === overId || folderId !== overId) return null;

      const activeIndex = folders.findIndex((f) => f.id === activeId);
      const overIndex = folders.findIndex((f) => f.id === overId);
      if (activeIndex === -1 || overIndex === -1) return null;

      // dragging down shows below, up shows above
      return activeIndex < overIndex ? 'below' : 'above';
    },
    [activeId, overId, folders],
  );

  // Render without nested DndContext when sorting is disabled (1 folder) or when
  // a bookmark is being dragged. The nested DndContext for folder sorting captures
  // useDroppable registrations, making folder drop targets invisible to the outer
  // BookmarkDndProvider. Switching to FolderItem during bookmark drags ensures
  // drop targets register with the outer context.
  if (!isSortingEnabled || isBookmarkBeingDragged) {
    return (
      <ul id="folders" className="list-reset" aria-label={t`Folders`}>
        {folders.map((folder) => (
          <FolderItem
            key={folder.id}
            folder={folder}
            isSelected={currentFolderId === folder.id}
          />
        ))}
      </ul>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <SortableContext items={folderIds} strategy={verticalListSortingStrategy}>
        <ul id="folders" className="list-reset" aria-label={t`Folders`}>
          {folders.map((folder) => (
            <SortableFolderItem
              key={folder.id}
              folder={folder}
              isSelected={currentFolderId === folder.id}
              dropIndicatorPosition={getDropIndicatorPosition(folder.id)}
              justMoved={folder.id === justMovedId}
              suppressTransition={justMovedId !== null && folder.id !== justMovedId}
            />
          ))}
        </ul>
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {activeFolder && <FolderDragOverlay folder={activeFolder} />}
      </DragOverlay>
    </DndContext>
  );
}

export default SortableFolderList;
